/**
 * Lift2Fit — coaching workspace: All Programmes for one client.
 *
 * The full block list the coach's client page links to instead of embedding:
 * every block the COACH wrote for this client — the API filters the list to
 * `coach_owned` in the coach scope, and the lifter's own blocks never appear —
 * with the dates it ran, its length and cadence, what was actually logged
 * inside it, and where it came from. Activating one re-archives whatever is
 * running — history is never rewritten.
 */
import { useState } from 'react';
import { api } from '@/lib/api';
import { ArrowRightIcon, PlusIcon } from '@/lib/icons';
import { AllProgrammesPanel } from '../all-programmes';
import { LiveMessage, LoadingPanel, Panel, ScreenHeader, TrackBadge } from '../components';
import { useAsync } from '../hooks';
import { Link, navigate } from '../router';
import { CRM_EYEBROW } from './shared';

export function CrmClientProgrammesScreen({ clientId }: { clientId: string }) {
  const detailState = useAsync(() => api.coachTrainee(clientId), [clientId]);
  const historyState = useAsync(() => api.programmePlans(clientId), [clientId]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'success' | 'danger'>('success');

  const client = detailState.data;
  const blocks = historyState.data ?? [];

  const activate = async (planId: string, name: string) => {
    setBusyId(planId);
    setMessage(null);
    const result = await api.activateProgramme(planId, clientId);
    setBusyId(null);
    if (result.problem) {
      setTone('danger');
      setMessage(result.problem.detail ?? 'That block could not be activated.');
      return;
    }
    setTone('success');
    setMessage(`${name} is now ${client?.displayName ?? 'the client'}'s active block.`);
    historyState.reload();
  };

  /** Stop the block without replacing it: the client runs nothing until they start one. */
  const deactivate = async (planId: string, name: string) => {
    setBusyId(planId);
    setMessage(null);
    const result = await api.deactivateProgramme(planId, clientId);
    setBusyId(null);
    if (result.problem) {
      setTone('danger');
      setMessage(result.problem.detail ?? 'That block could not be deactivated.');
      return;
    }
    setTone('success');
    setMessage(`${name} is no longer running. ${client?.displayName ?? 'The client'} starts a block when they are ready.`);
    historyState.reload();
  };

  /** Delete an inactive block of the coach's (the active one is refused by the API). */
  const remove = async (planId: string, name: string) => {
    setBusyId(planId);
    setMessage(null);
    const result = await api.deleteProgrammePlan(planId, clientId);
    setBusyId(null);
    if (result.problem) {
      setTone('danger');
      setMessage(result.problem.detail ?? 'That block could not be deleted.');
      return;
    }
    setTone('success');
    setMessage(`${name} deleted.`);
    historyState.reload();
  };

  if (historyState.loading && blocks.length === 0) {
    return <LoadingPanel label="Loading their blocks" rows={4} />;
  }

  if (historyState.error && blocks.length === 0) {
    return (
      <Panel title="All Programmes">
        <p className="text-[13px] text-danger">{historyState.error}</p>
        <Link
          to={`/crm/clients/${encodeURIComponent(clientId)}`}
          className="inline-flex h-10 w-fit items-center border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
        >
          Back to the client
        </Link>
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="crm-client-programmes">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="All Programmes"
        badge={client ? <TrackBadge track={client.track} /> : undefined}
        lead={
          client
            ? `${client.displayName} · every block you wrote for them, newest first. Activating one puts it back on the Grid; the record of what ran stays.`
            : 'Every block you wrote for them, newest first.'
        }
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <Link
              to={`/crm/clients/${encodeURIComponent(clientId)}`}
              data-testid="crm-programmes-back"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
            >
              {client ? `Back to ${client.displayName.split(' ')[0]}` : 'Back to the client'}
            </Link>
          </span>
        }
      />

      <LiveMessage message={message} tone={tone} />

      <AllProgrammesPanel
        audience="coach"
        blocks={blocks}
        busyId={busyId}
        subtitle="Every block you wrote for this client, newest first. Activate one to put it back on the Grid; deactivate the running one to stop it; delete a block you no longer need."
        onView={(entry) =>
          navigate(`/crm/clients/${encodeURIComponent(clientId)}/blocks/${encodeURIComponent(entry.planId)}`)
        }
        onActivate={(entry) => void activate(entry.planId, entry.name)}
        onDeactivate={(entry) => void deactivate(entry.planId, entry.name)}
        onDelete={(entry) => void remove(entry.planId, entry.name)}
      />

      <Panel title="Write the next block" subtitle="Start from an earlier block or from empty, on the client's page.">
        <Link
          to={`/crm/clients/${encodeURIComponent(clientId)}`}
          data-testid="crm-programmes-new-block"
          className="inline-flex h-11 w-fit items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
        >
          <PlusIcon className="text-[13px]" /> New Block
          <ArrowRightIcon className="text-[12px]" />
        </Link>
      </Panel>
    </div>
  );
}
